import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { 
  ShoppingBag, MapPin, Truck, CreditCard, Check, 
  ChevronRight, ArrowLeft, Plus, Loader2, Package, Shield, Percent, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../components/ui/sonner';
import ProductRecommendations from '../components/ProductRecommendations';
import EmbeddedPaymentForm from '../components/EmbeddedPaymentForm';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

// Shipping costs configuration
const SHIPPING_CONFIG = {
  freeThreshold: 250,
  standardCost: 15.99
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { cart, loading: cartLoading, fetchCart } = useCart();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [addresses, setAddresses] = useState([]);
  const [selectedShippingAddress, setSelectedShippingAddress] = useState(null);
  const [selectedBillingAddress, setSelectedBillingAddress] = useState(null);
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [shippingMethod, setShippingMethod] = useState('standard');
  const [orderNote, setOrderNote] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [useEmbeddedPayment, setUseEmbeddedPayment] = useState(true); // Default to embedded
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [newAddress, setNewAddress] = useState({
    label: 'Domicile',
    first_name: '',
    last_name: '',
    address_line1: '',
    postal_code: '',
    city: '',
    phone: ''
  });

  // Get TVA info from cart (calculated on backend with split rates)
  const tvaRate = cart?.tva_rate || 20;
  const tvaBreakdown = cart?.tva_breakdown || null;
  const subtotalHT = cart?.total_ht || (cart?.total ? cart.total / 1.20 : 0);
  const shippingCost = (cart?.total || 0) >= SHIPPING_CONFIG.freeThreshold ? 0 : SHIPPING_CONFIG.standardCost;
  
  // Shipping follows the product TVA rate (10% if installation, else 20%)
  const productTvaRate = tvaBreakdown?.products?.rate || (typeof tvaRate === 'number' ? tvaRate : 20);
  const shippingHT = shippingCost / (1 + productTvaRate / 100);
  const totalHT = subtotalHT + shippingHT;
  
  // Calculate total TVA with split rates
  const tvaAmountFromCart = cart?.tva_amount || 0;
  const shippingTva = shippingHT * (productTvaRate / 100);
  const tvaAmount = tvaAmountFromCart + shippingTva;
  const total = cart?.total + shippingCost || 0;

  // Check if cart has subscriptions (requires hosted checkout for SEPA)
  const hasSubscriptions = cart?.items?.some(item => 
    item.services?.some(s => s.price_type === 'monthly' || s.price_type === 'annual')
  ) || false;

  // Calculate products-only total for embedded payment (subscriptions excluded)
  const productsOnlyTotal = cart?.items?.reduce((sum, item) => {
    const productPrice = (item.price || 0) * (item.quantity || 1);
    const accessoriesPrice = item.accessories?.reduce((acc, a) => acc + (a.price || 0) * (a.quantity || 1), 0) || 0;
    const oneTimeServices = item.services?.filter(s => s.price_type === 'one_time' || !s.price_type)
      .reduce((acc, s) => acc + (s.price || 0), 0) || 0;
    return sum + productPrice + accessoriesPrice + oneTimeServices;
  }, 0) || 0;

  // Fetch user addresses
  useEffect(() => {
    if (isAuthenticated) {
      fetchAddresses();
    }
  }, [isAuthenticated]);

  // Redirect to auth if not logged in
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/auth?redirect=/checkout');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Redirect if cart is empty
  useEffect(() => {
    if (!cartLoading && (!cart?.items || cart.items.length === 0)) {
      navigate('/shop');
    }
  }, [cartLoading, cart, navigate]);

  const fetchAddresses = async () => {
    try {
      const response = await fetch(`${API}/auth/addresses`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Handle both array and {addresses: [...]} formats
        const addressList = Array.isArray(data) ? data : (data.addresses || []);
        setAddresses(addressList);
        // Auto-select default or first address
        if (addressList.length > 0) {
          const defaultAddr = addressList.find(a => a.is_default) || addressList[0];
          setSelectedShippingAddress(defaultAddr.address_id);
          setSelectedBillingAddress(defaultAddr.address_id);
        }
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
    }
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/auth/addresses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newAddress)
      });
      
      if (response.ok) {
        const data = await response.json();
        // Handle {address: {...}} format from backend
        const addr = data.address || data;
        setAddresses([...addresses, addr]);
        setSelectedShippingAddress(addr.address_id);
        if (sameAsBilling) {
          setSelectedBillingAddress(addr.address_id);
        }
        setShowNewAddressForm(false);
        setNewAddress({
          label: 'Domicile',
          first_name: '',
          last_name: '',
          address_line1: '',
          postal_code: '',
          city: '',
          phone: ''
        });
      }
    } catch (error) {
      console.error('Error adding address:', error);
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    try {
      // Create checkout session with additional data
      const response = await fetch(`${API}/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          origin_url: window.location.origin,
          shipping_address_id: selectedShippingAddress,
          billing_address_id: sameAsBilling ? selectedShippingAddress : selectedBillingAddress,
          shipping_method: shippingMethod,
          shipping_cost: shippingCost,
          order_note: orderNote
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Erreur création session');
      }

      const data = await response.json();
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Erreur lors de la création du paiement. Veuillez réessayer.');
    } finally {
      setProcessing(false);
    }
  };

  // Handler for embedded payment success
  const handleEmbeddedPaymentSuccess = (data) => {
    setPaymentCompleted(true);
    toast.success('Paiement effectué avec succès !');
    // Redirect to success page after a short delay
    setTimeout(() => {
      navigate(`/checkout/success?order_id=${data.order_id}`);
    }, 1500);
  };

  // Handler for embedded payment error
  const handleEmbeddedPaymentError = (error) => {
    toast.error(error?.message || 'Erreur lors du paiement. Veuillez réessayer.');
  };

  const steps = [
    { id: 1, name: 'Panier', icon: ShoppingBag },
    { id: 2, name: 'Adresses', icon: MapPin },
    { id: 3, name: 'Livraison', icon: Truck },
    { id: 4, name: 'Paiement', icon: CreditCard }
  ];

  const canProceed = () => {
    switch (currentStep) {
      case 1: return cart?.items?.length > 0;
      case 2: return selectedShippingAddress && (sameAsBilling || selectedBillingAddress);
      case 3: return shippingMethod;
      default: return true;
    }
  };

  const getSelectedAddress = (addressId) => {
    return addresses.find(a => a.address_id === addressId);
  };

  if (authLoading || cartLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-fgs-bg py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/shop" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Continuer mes achats
          </Link>
          <h1 className="font-heading text-2xl text-white">Finaliser ma commande</h1>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <button
                  onClick={() => step.id < currentStep && setCurrentStep(step.id)}
                  disabled={step.id > currentStep}
                  className={`flex flex-col items-center gap-2 transition-all ${
                    step.id <= currentStep ? 'text-emerald-400' : 'text-white/60'
                  } ${step.id < currentStep ? 'cursor-pointer hover:text-emerald-400-hover' : ''}`}
                  data-testid={`checkout-step-${step.id}`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all ${
                    step.id < currentStep 
                      ? 'bg-emerald-500 border-fgs-primary text-black' 
                      : step.id === currentStep 
                        ? 'border-fgs-primary bg-fgs-surface' 
                        : 'border-white/5 bg-fgs-surface'
                  }`}>
                    {step.id < currentStep ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <step.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className="text-sm font-medium hidden sm:block">{step.name}</span>
                </button>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    step.id < currentStep ? 'bg-emerald-500' : 'bg-fgs-border'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Step 1: Cart Review */}
            {currentStep === 1 && (
              <div className="bg-fgs-surface border border-white/5 rounded-xl p-6" data-testid="checkout-step-cart">
                <h2 className="font-heading text-xl text-white mb-6 flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-emerald-400" />
                  Votre panier
                </h2>
                
                <div className="space-y-4">
                  {cart?.items?.map((item) => (
                    <div key={`${item.product_id}-${item.color}`} className="p-4 bg-fgs-bg rounded-lg border border-white/5">
                      <div className="flex gap-4">
                        <div className="w-20 h-20 bg-white/5 rounded-md overflow-hidden flex-shrink-0">
                          <img 
                            src={item.images?.[item.color] || item.image_url || item.product?.images?.[item.color] || item.product?.image_url} 
                            alt={item.name || item.product?.name}
                            className="w-full h-full object-contain p-2"
                          />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-white">{item.name || item.product?.name}</h3>
                          <p className="text-sm text-white/60 capitalize">
                            Couleur: {item.color === 'black' ? 'Noir' : 'Blanc'}
                          </p>
                          <p className="text-sm text-white/60">Quantité: {item.quantity}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-emerald-400 font-mono">
                            {((item.price || item.product?.price) * item.quantity).toFixed(2)} €
                          </p>
                        </div>
                      </div>
                      
                      {/* Display Services */}
                      {item.services && item.services.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-white/5 space-y-2">
                          {item.services.map((service, sIdx) => (
                            <div key={sIdx} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                {service.price_type === 'annual' || service.price_type === 'monthly' ? (
                                  <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded font-medium">
                                    Abonnement
                                  </span>
                                ) : (
                                  <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded font-medium">
                                    Service
                                  </span>
                                )}
                                <span className="text-white/70">{service.name}</span>
                              </div>
                              <span className={`font-mono ${
                                service.price_type === 'annual' || service.price_type === 'monthly' 
                                  ? 'text-blue-400' 
                                  : 'text-emerald-400'
                              }`}>
                                {service.price?.toFixed(2)} €
                                {service.price_type === 'annual' && <span className="text-white/40">/an</span>}
                                {service.price_type === 'monthly' && <span className="text-white/40">/mois</span>}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Display Accessories */}
                      {item.accessories && item.accessories.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-white/5 space-y-2">
                          {item.accessories.map((accessory, aIdx) => (
                            <div key={aIdx} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded font-medium">
                                  Accessoire
                                </span>
                                <span className="text-white/70">
                                  {accessory.name} {accessory.quantity > 1 && `× ${accessory.quantity}`}
                                </span>
                              </div>
                              <span className="text-purple-400 font-mono">
                                {((accessory.price || 0) * (accessory.quantity || 1)).toFixed(2)} €
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* TVA Info Banner */}
                {cart?.has_installation && cart?.installation_confirmed_2_years && (
                  <div className="mt-6 bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <Percent className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="text-green-500 font-medium">TVA réduite à 10% appliquée</p>
                        <p className="text-xs text-white/60">Installation professionnelle avec logement de +2 ans confirmé</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {cart?.items?.length > 0 && (cart?.items?.[0]?.category || cart?.items?.[0]?.product?.category) && (
                  <div className="mt-8 pt-6 border-t border-white/5">
                    <h3 className="font-heading text-lg text-white mb-4">Complétez votre système</h3>
                    <ProductRecommendations 
                      category={cart.items[0].category || cart.items[0].product?.category}
                      currentProductId={cart.items[0].product_id}
                      excludeProductIds={cart.items.map(item => item.product_id)}
                      maxItems={3}
                      compact={true}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Addresses */}
            {currentStep === 2 && (
              <div className="bg-fgs-surface border border-white/5 rounded-xl p-6" data-testid="checkout-step-addresses">
                <h2 className="font-heading text-xl text-white mb-6 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-emerald-400" />
                  Adresses
                </h2>

                {/* Shipping Address */}
                <div className="mb-8">
                  <h3 className="font-medium text-white mb-4">Adresse de livraison</h3>
                  
                  {addresses.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {addresses.map((addr) => (
                        <label
                          key={addr.address_id}
                          className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            selectedShippingAddress === addr.address_id
                              ? 'border-fgs-primary bg-emerald-500/10'
                              : 'border-white/5 hover:border-fgs-primary/50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="shippingAddress"
                            value={addr.address_id}
                            checked={selectedShippingAddress === addr.address_id}
                            onChange={(e) => {
                              setSelectedShippingAddress(e.target.value);
                              if (sameAsBilling) {
                                setSelectedBillingAddress(e.target.value);
                              }
                            }}
                            className="sr-only"
                          />
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-white">{addr.label}</p>
                              <p className="text-sm text-white/60 mt-1">
                                {addr.first_name} {addr.last_name}
                              </p>
                              <p className="text-sm text-white/60">{addr.address_line1}</p>
                              {addr.address_line2 && (
                                <p className="text-sm text-white/60">{addr.address_line2}</p>
                              )}
                              <p className="text-sm text-white/60">{addr.postal_code} {addr.city}</p>
                              {addr.phone && (
                                <p className="text-sm text-white/60 mt-1">{addr.phone}</p>
                              )}
                            </div>
                            {selectedShippingAddress === addr.address_id && (
                              <Check className="w-5 h-5 text-emerald-400" />
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-white/60">Aucune adresse enregistrée</p>
                  )}

                  {/* Add New Address */}
                  {!showNewAddressForm ? (
                    <Button
                      variant="outline"
                      onClick={() => setShowNewAddressForm(true)}
                      className="mt-4 border-white/5 text-white hover:bg-white/5"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter une adresse
                    </Button>
                  ) : (
                    <form onSubmit={handleAddAddress} className="mt-4 p-4 bg-fgs-bg rounded-lg border border-white/5">
                      <h4 className="font-medium text-white mb-4">Nouvelle adresse</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="label">Type d'adresse</Label>
                          <select
                            id="label"
                            value={newAddress.label}
                            onChange={(e) => setNewAddress({...newAddress, label: e.target.value})}
                            className="w-full mt-1 bg-fgs-surface border border-white/5 rounded-md px-3 py-2 text-white"
                          >
                            <option value="Domicile">Domicile</option>
                            <option value="Bureau">Bureau</option>
                            <option value="Autre">Autre</option>
                          </select>
                        </div>
                        <div>
                          <Label htmlFor="phone">Téléphone</Label>
                          <Input
                            id="phone"
                            value={newAddress.phone}
                            onChange={(e) => setNewAddress({...newAddress, phone: e.target.value})}
                            className="mt-1 bg-fgs-surface border-white/5"
                            placeholder="06 12 34 56 78"
                          />
                        </div>
                        <div>
                          <Label htmlFor="first_name">Prénom *</Label>
                          <Input
                            id="first_name"
                            required
                            value={newAddress.first_name}
                            onChange={(e) => setNewAddress({...newAddress, first_name: e.target.value})}
                            className="mt-1 bg-fgs-surface border-white/5"
                          />
                        </div>
                        <div>
                          <Label htmlFor="last_name">Nom *</Label>
                          <Input
                            id="last_name"
                            required
                            value={newAddress.last_name}
                            onChange={(e) => setNewAddress({...newAddress, last_name: e.target.value})}
                            className="mt-1 bg-fgs-surface border-white/5"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label htmlFor="address_line1">Adresse *</Label>
                          <Input
                            id="address_line1"
                            required
                            value={newAddress.address_line1}
                            onChange={(e) => setNewAddress({...newAddress, address_line1: e.target.value})}
                            className="mt-1 bg-fgs-surface border-white/5"
                            placeholder="123 rue Example"
                          />
                        </div>
                        <div>
                          <Label htmlFor="postal_code">Code postal *</Label>
                          <Input
                            id="postal_code"
                            required
                            value={newAddress.postal_code}
                            onChange={(e) => setNewAddress({...newAddress, postal_code: e.target.value})}
                            className="mt-1 bg-fgs-surface border-white/5"
                            placeholder="34000"
                          />
                        </div>
                        <div>
                          <Label htmlFor="city">Ville *</Label>
                          <Input
                            id="city"
                            required
                            value={newAddress.city}
                            onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
                            className="mt-1 bg-fgs-surface border-white/5"
                            placeholder="Montpellier"
                          />
                        </div>
                      </div>
                      <div className="flex gap-3 mt-4">
                        <Button type="submit" className="bg-emerald-500 hover:bg-emerald-500-hover">
                          Enregistrer
                        </Button>
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => setShowNewAddressForm(false)}
                          className="border-white/5 text-white"
                        >
                          Annuler
                        </Button>
                      </div>
                    </form>
                  )}
                </div>

                {/* Billing Address */}
                <div className="pt-6 border-t border-white/5">
                  <h3 className="font-medium text-white mb-4">Adresse de facturation</h3>
                  
                  <label className="flex items-center gap-3 cursor-pointer mb-4">
                    <input
                      type="checkbox"
                      checked={sameAsBilling}
                      onChange={(e) => {
                        setSameAsBilling(e.target.checked);
                        if (e.target.checked) {
                          setSelectedBillingAddress(selectedShippingAddress);
                        }
                      }}
                      className="w-5 h-5 rounded border-white/5 bg-fgs-surface text-emerald-400 focus:ring-fgs-primary"
                    />
                    <span className="text-white/60">Identique à l'adresse de livraison</span>
                  </label>

                  {!sameAsBilling && addresses.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {addresses.map((addr) => (
                        <label
                          key={addr.address_id}
                          className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            selectedBillingAddress === addr.address_id
                              ? 'border-fgs-primary bg-emerald-500/10'
                              : 'border-white/5 hover:border-fgs-primary/50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="billingAddress"
                            value={addr.address_id}
                            checked={selectedBillingAddress === addr.address_id}
                            onChange={(e) => setSelectedBillingAddress(e.target.value)}
                            className="sr-only"
                          />
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-white">{addr.label}</p>
                              <p className="text-sm text-white/60 mt-1">
                                {addr.first_name} {addr.last_name}
                              </p>
                              <p className="text-sm text-white/60">{addr.address_line1}</p>
                              {addr.address_line2 && (
                                <p className="text-sm text-white/60">{addr.address_line2}</p>
                              )}
                              <p className="text-sm text-white/60">{addr.postal_code} {addr.city}</p>
                            </div>
                            {selectedBillingAddress === addr.address_id && (
                              <Check className="w-5 h-5 text-emerald-400" />
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Shipping & Options */}
            {currentStep === 3 && (
              <div className="space-y-6" data-testid="checkout-step-shipping">
                {/* Shipping Method */}
                <div className="bg-fgs-surface border border-white/5 rounded-xl p-6">
                  <h2 className="font-heading text-xl text-white mb-6 flex items-center gap-2">
                    <Truck className="w-5 h-5 text-emerald-400" />
                    Mode de livraison
                  </h2>

                  <div className="space-y-4">
                    <label
                      className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all flex items-center justify-between ${
                        shippingMethod === 'standard'
                          ? 'border-fgs-primary bg-emerald-500/10'
                          : 'border-white/5 hover:border-fgs-primary/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="shippingMethod"
                        value="standard"
                        checked={shippingMethod === 'standard'}
                        onChange={(e) => setShippingMethod(e.target.value)}
                        className="sr-only"
                      />
                      <div className="flex items-center gap-4">
                        <Package className="w-8 h-8 text-emerald-400" />
                        <div>
                          <p className="font-medium text-white">Livraison Standard</p>
                          <p className="text-sm text-white/60">Livré sous 3-5 jours ouvrés</p>
                        </div>
                      </div>
                      <div className="text-right">
                        {(cart?.total || 0) >= SHIPPING_CONFIG.freeThreshold ? (
                          <div>
                            <p className="text-emerald-400 font-semibold">GRATUIT</p>
                            <p className="text-xs text-white/60 line-through">{SHIPPING_CONFIG.standardCost.toFixed(2)} €</p>
                          </div>
                        ) : (
                          <p className="font-mono text-white">{SHIPPING_CONFIG.standardCost.toFixed(2)} €</p>
                        )}
                      </div>
                    </label>

                    {(cart?.total || 0) < SHIPPING_CONFIG.freeThreshold && (
                      <div className="p-4 bg-emerald-500/10 rounded-lg border border-fgs-primary/30">
                        <p className="text-sm text-emerald-400">
                          <Shield className="w-4 h-4 inline mr-2" />
                          Plus que <strong>{(SHIPPING_CONFIG.freeThreshold - (cart?.total || 0)).toFixed(2)} €</strong> pour bénéficier de la livraison gratuite !
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Order Note */}
                <div className="bg-fgs-surface border border-white/5 rounded-xl p-6">
                  <h3 className="font-medium text-white mb-4">Instructions de livraison (optionnel)</h3>
                  <textarea
                    value={orderNote}
                    onChange={(e) => setOrderNote(e.target.value)}
                    placeholder="Ex: Code de l'immeuble, étage, instructions spéciales..."
                    className="w-full bg-fgs-bg border border-white/5 rounded-lg px-4 py-3 text-white placeholder:text-white/60 focus:outline-none focus:border-fgs-primary resize-none"
                    rows={3}
                  />
                </div>

                {/* Recommendations */}
                {cart?.items?.length > 0 && cart?.items?.[0]?.product?.category && (
                  <div className="bg-fgs-surface border border-white/5 rounded-xl p-6">
                    <h3 className="font-heading text-lg text-white mb-4">Dernière chance ! Complétez votre système</h3>
                    <ProductRecommendations 
                      category={cart.items[0].product.category}
                      currentProductId={cart.items[0].product_id}
                      excludeProductIds={cart.items.map(item => item.product_id)}
                      maxItems={4}
                      compact={true}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Payment Summary */}
            {currentStep === 4 && (
              <div className="bg-fgs-surface border border-white/5 rounded-xl p-6" data-testid="checkout-step-payment">
                <h2 className="font-heading text-xl text-white mb-6 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-emerald-400" />
                  Récapitulatif & Paiement
                </h2>

                {/* Order Summary */}
                <div className="space-y-6">
                  {/* Products */}
                  <div>
                    <h3 className="font-medium text-white mb-3">Articles ({cart?.items?.length})</h3>
                    <div className="space-y-2">
                      {cart?.items?.map((item) => (
                        <div key={`${item.product_id}-${item.color}`} className="flex justify-between text-sm">
                          <span className="text-white/60">
                            {item.name || item.product?.name} × {item.quantity}
                          </span>
                          <span className="text-white font-mono">
                            {((item.price || item.product?.price) * item.quantity).toFixed(2)} €
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Shipping Address */}
                  <div className="pt-4 border-t border-white/5">
                    <h3 className="font-medium text-white mb-2">Adresse de livraison</h3>
                    {selectedShippingAddress && (
                      <div className="text-sm text-white/60">
                        {(() => {
                          const addr = getSelectedAddress(selectedShippingAddress);
                          return addr ? (
                            <>
                              <p>{addr.first_name} {addr.last_name}</p>
                              <p>{addr.address_line1}</p>
                              {addr.address_line2 && <p>{addr.address_line2}</p>}
                              <p>{addr.postal_code} {addr.city}</p>
                            </>
                          ) : null;
                        })()}
                      </div>
                    )}
                  </div>

                  {/* Billing Address */}
                  {!sameAsBilling && selectedBillingAddress && (
                    <div className="pt-4 border-t border-white/5">
                      <h3 className="font-medium text-white mb-2">Adresse de facturation</h3>
                      <div className="text-sm text-white/60">
                        {(() => {
                          const addr = getSelectedAddress(selectedBillingAddress);
                          return addr ? (
                            <>
                              <p>{addr.first_name} {addr.last_name}</p>
                              <p>{addr.address_line1}</p>
                              {addr.address_line2 && <p>{addr.address_line2}</p>}
                              <p>{addr.postal_code} {addr.city}</p>
                            </>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Shipping Method */}
                  <div className="pt-4 border-t border-white/5">
                    <h3 className="font-medium text-white mb-2">Mode de livraison</h3>
                    <p className="text-sm text-white/60">Livraison Standard (3-5 jours ouvrés)</p>
                  </div>

                  {/* Order Note */}
                  {orderNote && (
                    <div className="pt-4 border-t border-white/5">
                      <h3 className="font-medium text-white mb-2">Instructions</h3>
                      <p className="text-sm text-white/60">{orderNote}</p>
                    </div>
                  )}
                </div>

                {/* Security Badge */}
                <div className="mt-6 p-4 bg-fgs-bg rounded-lg border border-white/5 flex items-center gap-3">
                  <Shield className="w-8 h-8 text-emerald-400 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="text-white font-medium">Paiement 100% sécurisé</p>
                    <p className="text-white/60">Vos données sont protégées par Stripe</p>
                  </div>
                </div>

                {/* Embedded Payment Form - Now works for ALL carts (with or without subscriptions) */}
                {useEmbeddedPayment && !paymentCompleted && (
                  <div className="mt-6 pt-6 border-t border-white/5">
                    {/* Info banner for carts with subscriptions */}
                    {hasSubscriptions && (
                      <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-blue-400 font-bold text-sm">1</span>
                          </div>
                          <div>
                            <p className="text-blue-400 font-medium">Paiement des produits</p>
                            <p className="text-white/50 text-xs mt-1">
                              Payez vos produits maintenant. L'abonnement 4G sera configuré à l'étape suivante.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <EmbeddedPaymentForm
                      cartTotal={productsOnlyTotal + shippingCost}
                      cartId={cart?.cart_id}
                      onPaymentSuccess={handleEmbeddedPaymentSuccess}
                      onPaymentError={handleEmbeddedPaymentError}
                      hasSubscriptions={hasSubscriptions}
                    />
                  </div>
                )}

                {/* Payment Completed Message */}
                {paymentCompleted && (
                  <div className="mt-6 p-6 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-center">
                    <Check className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                    <p className="text-white font-semibold text-lg">Paiement effectué !</p>
                    <p className="text-white/60 text-sm mt-1">Redirection en cours...</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-fgs-surface border border-white/5 rounded-xl p-6 sticky top-24">
              <h3 className="font-heading text-lg text-white mb-4">Résumé</h3>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/60">Sous-total HT</span>
                  <span className="text-white font-mono">{subtotalHT.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Livraison</span>
                  <span className={`font-mono ${shippingCost === 0 ? 'text-emerald-400' : 'text-white'}`}>
                    {shippingCost === 0 ? 'GRATUIT' : `${shippingCost.toFixed(2)} €`}
                  </span>
                </div>
                {/* TVA Display - handle mixed rates */}
                {tvaBreakdown && tvaBreakdown.subscriptions_4g?.base_ht > 0 ? (
                  // Mixed TVA: products at 10% or 20%, subscriptions always at 20%
                  <>
                    {tvaBreakdown.products?.base_ht > 0 && (
                      <div className="flex justify-between">
                        <span className="text-white/60 flex items-center gap-1">
                          TVA {tvaBreakdown.products.rate}% (produits)
                          {tvaBreakdown.products.rate === 10 && <Percent className="w-3 h-3 text-green-500" />}
                        </span>
                        <span className={`font-mono ${tvaBreakdown.products.rate === 10 ? 'text-green-500' : 'text-white'}`}>
                          {(tvaBreakdown.products.amount + shippingTva).toFixed(2)} €
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-white/60">TVA 20% (forfait 4G)</span>
                      <span className="text-white font-mono">
                        {tvaBreakdown.subscriptions_4g.amount?.toFixed(2)} €
                      </span>
                    </div>
                  </>
                ) : (
                  // Single TVA rate
                  <div className="flex justify-between">
                    <span className="text-white/60 flex items-center gap-1">
                      TVA ({typeof tvaRate === 'number' ? `${tvaRate}%` : '20%'})
                      {tvaRate === 10 && <Percent className="w-3 h-3 text-green-500" />}
                    </span>
                    <span className={`font-mono ${tvaRate === 10 ? 'text-green-500' : 'text-white'}`}>
                      {tvaAmount.toFixed(2)} €
                    </span>
                  </div>
                )}
                
                {/* TVA 10% Badge - show when installation with 2 years confirmed */}
                {cart?.has_installation && tvaBreakdown?.products?.rate === 10 && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded p-2 text-center">
                    <span className="text-green-500 text-xs font-medium">
                      TVA réduite 10% appliquée
                    </span>
                  </div>
                )}
                
                {/* Promo Code Display */}
                {cart?.promo_discount > 0 && (
                  <>
                    <div className="flex justify-between text-white/60">
                      <span>Sous-total TTC</span>
                      <span className="font-mono line-through">
                        {cart?.subtotal_before_promo?.toFixed(2)} €
                      </span>
                    </div>
                    <div className="flex justify-between text-emerald-400">
                      <span className="flex items-center gap-1">
                        Réduction ({cart?.promo_code})
                      </span>
                      <span className="font-mono">
                        -{cart?.promo_discount?.toFixed(2)} €
                      </span>
                    </div>
                  </>
                )}
                
                <div className="pt-3 border-t border-white/5 flex justify-between">
                  <span className="text-white font-medium">Total TTC</span>
                  <span className="text-emerald-400 font-mono text-xl">{total.toFixed(2)} €</span>
                </div>

                {/* SPLIT PAYMENT DETAILS - Show when cart has subscriptions */}
                {(() => {
                  const hasSubscriptions = cart?.items?.some(item => 
                    item.services?.some(s => s.price_type === 'monthly' || s.price_type === 'annual')
                  );
                  
                  if (!hasSubscriptions) return null;
                  
                  // Calculate products total (one-time)
                  const productsTotal = cart?.items?.reduce((sum, item) => {
                    const productPrice = (item.price || 0) * (item.quantity || 1);
                    const accessoriesPrice = item.accessories?.reduce((acc, a) => acc + (a.price || 0) * (a.quantity || 1), 0) || 0;
                    const oneTimeServices = item.services?.filter(s => s.price_type === 'one_time' || !s.price_type)
                      .reduce((acc, s) => acc + (s.price || 0), 0) || 0;
                    return sum + productPrice + accessoriesPrice + oneTimeServices;
                  }, 0) || 0;
                  
                  // Calculate subscription total
                  const subscriptionTotal = cart?.items?.reduce((sum, item) => {
                    const monthlyServices = item.services?.filter(s => s.price_type === 'monthly' || s.price_type === 'annual')
                      .reduce((acc, s) => acc + (s.price || 0), 0) || 0;
                    return sum + monthlyServices;
                  }, 0) || 0;
                  
                  // Get subscription period
                  const subscriptionPeriod = cart?.items?.some(item => 
                    item.services?.some(s => s.price_type === 'annual')
                  ) ? '/an' : '/mois';
                  
                  return (
                    <div className="mt-4 bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 space-y-3">
                      <div className="flex items-center gap-2 text-blue-400 text-sm font-medium">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        Détail du paiement en 2 étapes
                      </div>
                      
                      {/* Payment 1: Products */}
                      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="bg-emerald-500 text-black text-xs font-bold px-2 py-0.5 rounded">ÉTAPE 1</span>
                              <span className="text-white font-medium">Aujourd'hui</span>
                            </div>
                            <p className="text-white/60 text-sm">Produits & Installation</p>
                            <p className="text-white/40 text-xs mt-1">CB, Klarna 3x sans frais, ou SEPA</p>
                          </div>
                          <div className="text-right">
                            <span className="text-emerald-400 font-mono text-lg font-bold">{productsTotal.toFixed(2)} €</span>
                            {productsTotal >= 35 && (
                              <p className="text-[#FFB3C7] text-xs mt-1">ou {(productsTotal / 3).toFixed(2)} €/mois × 3</p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Payment 2: Subscription */}
                      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded">ÉTAPE 2</span>
                              <span className="text-white font-medium">Après l'achat</span>
                            </div>
                            <p className="text-white/60 text-sm">Abonnement 4G</p>
                            <p className="text-white/40 text-xs mt-1">Prélèvement SEPA (pas de carte)</p>
                          </div>
                          <div className="text-right">
                            <span className="text-blue-400 font-mono text-lg font-bold">{subscriptionTotal.toFixed(2)} €</span>
                            <span className="text-white/40 text-sm">{subscriptionPeriod}</span>
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-white/40 text-xs text-center">
                        L'abonnement sera configuré après le paiement des produits
                      </p>
                    </div>
                  );
                })()}

                {/* Klarna Pay in 3 Option - Only for one-time payments */}
                {(() => {
                  // Check if cart has subscriptions
                  const hasSubscriptions = cart?.items?.some(item => 
                    item.services?.some(s => s.price_type === 'monthly' || s.price_type === 'annual')
                  );
                  
                  // Calculate products-only total
                  const productsTotal = cart?.items?.reduce((sum, item) => {
                    const productPrice = (item.price || 0) * (item.quantity || 1);
                    const accessoriesPrice = item.accessories?.reduce((acc, a) => acc + (a.price || 0) * (a.quantity || 1), 0) || 0;
                    const oneTimeServices = item.services?.filter(s => s.price_type === 'one_time' || !s.price_type)
                      .reduce((acc, s) => acc + (s.price || 0), 0) || 0;
                    return sum + productPrice + accessoriesPrice + oneTimeServices;
                  }, 0) || 0;
                  
                  // Don't show separate Klarna box if split payment is shown (already included above)
                  if (hasSubscriptions) return null;
                  if (productsTotal < 35) return null;
                  
                  return (
                    <div className="mt-4 bg-[#FFB3C7]/10 border border-[#FFB3C7]/20 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#FFB3C7] rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-black font-black text-sm">K.</span>
                          </div>
                          <div>
                            <p className="text-white text-sm font-medium">Payer en 3x avec Klarna</p>
                            <p className="text-white/50 text-xs">Sans frais • Sélectionnable au paiement</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-[#FFB3C7] font-mono font-bold">{(productsTotal / 3).toFixed(2)} €</p>
                          <p className="text-white/40 text-xs">/mois × 3</p>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Navigation Buttons */}
              <div className="mt-6 space-y-3">
                {currentStep < 4 ? (
                  <Button
                    onClick={() => setCurrentStep(currentStep + 1)}
                    disabled={!canProceed()}
                    className="w-full bg-emerald-500 hover:bg-emerald-500-hover h-12 text-base font-semibold"
                    data-testid="checkout-next-button"
                  >
                    Continuer
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                ) : (
                  <>
                    {/* Show redirect button ONLY if embedded is disabled */}
                    {!useEmbeddedPayment && !paymentCompleted && (
                      <Button
                        onClick={handlePayment}
                        disabled={processing}
                        className="w-full bg-emerald-500 hover:bg-emerald-500-hover h-12 text-base font-semibold glow-primary-hover"
                        data-testid="checkout-pay-button"
                      >
                        {processing ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Redirection...
                          </>
                        ) : (
                          <>
                            <ExternalLink className="w-5 h-5 mr-2" />
                            Payer via Stripe {total.toFixed(2)} €
                          </>
                        )}
                      </Button>
                    )}
                    
                    {/* Toggle to use hosted checkout if embedded fails */}
                    {useEmbeddedPayment && !paymentCompleted && (
                      <button
                        onClick={() => setUseEmbeddedPayment(false)}
                        className="w-full text-center text-white/40 hover:text-white/60 text-xs py-2 transition-colors"
                      >
                        Problème ? Utiliser le paiement externe
                      </button>
                    )}
                  </>
                )}

                {currentStep > 1 && !paymentCompleted && (
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep(currentStep - 1)}
                    className="w-full border-white/5 text-white hover:bg-white/5"
                    data-testid="checkout-back-button"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour
                  </Button>
                )}
              </div>

              {/* Guarantees */}
              <div className="mt-6 pt-6 border-t border-white/5 space-y-3 text-sm">
                <div className="flex items-center gap-2 text-white/60">
                  <Shield className="w-4 h-4 text-emerald-400" />
                  Garantie 4 ans FGS
                </div>
                <div className="flex items-center gap-2 text-white/60">
                  <Package className="w-4 h-4 text-emerald-400" />
                  Livraison gratuite dès 250€
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
